import { AuthGuard, CheckPermissionsModes } from "@elit-hair/elit-tools";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { TbTable } from "react-icons/tb";

import { DIMENSION_DEFINITION_LIMIT } from "@/constants/dimensions";
import { PERMISSIONS } from "@/constants/permissions";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { NotAuthorizedPage } from "@/pages/not-authorized/-index";
import { useDimensionDefinitions } from "@/queries/dimension-definition.queries";
import type { DimensionDefinitionType } from "@/types/dimension-definition.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { AgentQualityMatrixTable } from "./components/agent-quality-matrix-table";
import { SettingsPanel } from "./components/settings-panel";
import { ThresholdsDialog } from "./components/thresholds-dialog";
import { TopBar } from "./components/top-bar";
import { useAgentQualityMatrixSettings } from "./hooks/use-agent-quality-matrix-settings";
import { useQueryErrorToast } from "./hooks/use-query-error-toast";
import { useAgentQualityMatrixReport } from "./queries/agent-quality-matrix.queries";
import type {
    AgentQualityMatrixDimension,
    AgentQualityMatrixRow,
    AgentQualityMatrixSortState,
    AgentQualityMatrixViewMode,
} from "./types";
import {
    AGENT_QUALITY_MATRIX_DIMENSION_IDS,
    AGENT_QUALITY_MATRIX_OVERALL_SCORE_DEFINITION_ID,
} from "./utils/constants";
import { resolveAgentQualityMatrixUtcRange } from "./utils/date-range.utils";
import {
    pickAgentQualityMatrixDimensions,
    pickOverallScoreDimension,
    sanitizeSelectedMatrixDimensionIds,
} from "./utils/matrix-dimensions.utils";
import { getAgentQualityMatrixQueryErrorMessage } from "./utils/query-error.utils";
import { buildSegments } from "./utils/segments";
import { getAgentUserId, getAgentsFromTeams, getTeamColorForAgent, getVisibleTeams } from "./utils/team-agents.utils";

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_AGENT_QUALITY_MATRIX)({
    component: AgentQualityMatrixPage,
    staticData: {
        config: {
            breadcrumb: {
                title: "agent_quality_matrix",
                to: APP_ROUTES_TO.PERFORMANCE_AGENT_QUALITY_MATRIX,
            },
            sidebar: {
                label: "agent_quality_matrix",
                icon: TbTable,
            },
            title: "agent_quality_matrix",
        },
        permissions: [PERMISSIONS.CALL_SVC_TEAM_LEAD_BASE, PERMISSIONS.CALL_SVC_ADMIN_BASE],
    },
});

const DIMENSION_SUBSCRIPTS = ["₁", "₂", "₃", "₄", "₅", "₆", "₇", "₈", "₉"];

function makeDimensionShortLabel(index: number): string {
    return `d${DIMENSION_SUBSCRIPTS[index] ?? `·${index + 1}`}`;
}

function mapDimensionsForTable(defs: DimensionDefinitionType[], selectedIds: string[]): AgentQualityMatrixDimension[] {
    if (selectedIds.length === 0) return [];

    const byId = new Map(defs.map((definition) => [definition.id, definition]));
    const ordered = AGENT_QUALITY_MATRIX_DIMENSION_IDS.filter((id) => selectedIds.includes(id))
        .map((id) => byId.get(id))
        .filter((definition): definition is DimensionDefinitionType => definition != null);

    return ordered.map((definition, idx) => ({
        id: definition.id,
        name: definition.name,
        shortLabel: makeDimensionShortLabel(idx),
        tags: definition.tags,
    }));
}

function AgentQualityMatrixPage() {
    const { t } = useTranslation();
    const { settings, setSettings } = useAgentQualityMatrixSettings();
    const [viewMode, setViewMode] = useState<AgentQualityMatrixViewMode>("day");
    const [isThresholdsOpen, setIsThresholdsOpen] = useState(false);
    const [sortState, setSortState] = useState<AgentQualityMatrixSortState>(null);

    const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 6);
        return { from: start, to: end };
    });

    const {
        data: teamsResponse,
        isError: isTeamsError,
        error: teamsError,
        errorUpdatedAt: teamsErrorUpdatedAt,
    } = useTeams();
    const teams = useMemo(() => teamsResponse?.data ?? [], [teamsResponse]);

    const teamsErrorDescription = useMemo(
        () => (teamsError ? getAgentQualityMatrixQueryErrorMessage(teamsError, t) : ""),
        [teamsError, t]
    );

    useQueryErrorToast({
        queryKey: "agent-quality-matrix-teams",
        isError: isTeamsError,
        error: teamsError,
        errorUpdatedAt: teamsErrorUpdatedAt,
        title: t("agent_quality_matrix_teams_load_error"),
        description: teamsErrorDescription,
    });

    const visibleTeams = useMemo(
        () => getVisibleTeams(teams, settings.selectedTeamIds),
        [teams, settings.selectedTeamIds]
    );

    const visibleAgents = useMemo(() => getAgentsFromTeams(visibleTeams), [visibleTeams]);

    const visibleAgentIds = useMemo(
        () => new Set(visibleAgents.map((member) => getAgentUserId(member))),
        [visibleAgents]
    );

    const dimensionsQuery = useDimensionDefinitions({
        page: 1,
        limit: DIMENSION_DEFINITION_LIMIT,
    });

    const dimensionsErrorDescription = useMemo(
        () => (dimensionsQuery.error ? getAgentQualityMatrixQueryErrorMessage(dimensionsQuery.error, t) : ""),
        [dimensionsQuery.error, t]
    );

    useQueryErrorToast({
        queryKey: "agent-quality-matrix-dimensions",
        isError: dimensionsQuery.isError,
        error: dimensionsQuery.error,
        errorUpdatedAt: dimensionsQuery.errorUpdatedAt,
        title: t("agent_quality_matrix_dimensions_load_error"),
        description: dimensionsErrorDescription,
    });

    const allDimensions = useMemo<DimensionDefinitionType[]>(() => {
        const data = dimensionsQuery.data?.data;
        return Array.isArray(data) ? data : [];
    }, [dimensionsQuery.data]);

    const overallDimension = useMemo(() => pickOverallScoreDimension(allDimensions), [allDimensions]);
    const selectableDimensions = useMemo(() => pickAgentQualityMatrixDimensions(allDimensions), [allDimensions]);

    const selectedDimensionIds = useMemo(
        () => sanitizeSelectedMatrixDimensionIds(settings.selectedDimensionIds),
        [settings.selectedDimensionIds]
    );

    const utcRange = useMemo(() => resolveAgentQualityMatrixUtcRange(dateRange, viewMode), [dateRange, viewMode]);

    const segments = useMemo(() => {
        if (!utcRange) return [];
        return buildSegments({ viewMode, from: utcRange.from, to: utcRange.to });
    }, [utcRange, viewMode]);

    const reportParams = useMemo(() => {
        if (!utcRange) return null;
        const startTime = utcRange.from.toISO();
        const endTime = utcRange.to.endOf("day").toISO();
        if (!startTime || !endTime) return null;

        return {
            startTime,
            endTime,
            scope: viewMode,
            userIds: settings.selectedAgentIds,
            dimensionIds: selectedDimensionIds,
            overallDimensionId: AGENT_QUALITY_MATRIX_OVERALL_SCORE_DEFINITION_ID,
        };
    }, [utcRange, viewMode, settings.selectedAgentIds, selectedDimensionIds]);

    const {
        data: reportResponse,
        isLoading: isReportLoading,
        isError: isReportError,
        error: reportError,
    } = useAgentQualityMatrixReport(reportParams);
    const reportData = reportResponse?.data;

    const reportErrorMessage = useMemo(
        () => (reportError ? getAgentQualityMatrixQueryErrorMessage(reportError, t) : ""),
        [reportError, t]
    );

    const tableDimensions = useMemo(
        () => mapDimensionsForTable(selectableDimensions, selectedDimensionIds),
        [selectableDimensions, selectedDimensionIds]
    );

    const agentRows = useMemo<AgentQualityMatrixRow[]>(() => {
        const selected = settings.selectedAgentIds.filter((userId) => visibleAgentIds.has(userId));
        if (selected.length === 0) return [];

        return selected.map((userId) => {
            const member = visibleAgents.find((agent) => getAgentUserId(agent) === userId);
            return {
                id: `agent-${userId}`,
                userId,
                name: member?.user?.name ?? userId,
                color: getTeamColorForAgent(visibleTeams, userId),
            };
        });
    }, [settings.selectedAgentIds, visibleAgentIds, visibleAgents, visibleTeams]);

    const canShowSelectedAverageColumn =
        settings.showSelectedAverage &&
        tableDimensions.length > 0 &&
        tableDimensions.length < selectableDimensions.length;

    const tableEmptyMessage = t("agent_quality_matrix_no_agents_selected");

    const handleSelectedTeamIdsChange = (selectedTeamIds: string[] | null) => {
        setSettings((prev) => {
            const nextVisibleAgents = getAgentsFromTeams(getVisibleTeams(teams, selectedTeamIds));
            const nextVisibleAgentIds = new Set(nextVisibleAgents.map((member) => getAgentUserId(member)));

            return {
                ...prev,
                selectedTeamIds,
                selectedAgentIds: prev.selectedAgentIds.filter((userId) => nextVisibleAgentIds.has(userId)),
            };
        });
    };

    return (
        <AuthGuard
            required={[PERMISSIONS.CALL_SVC_TEAM_LEAD_BASE, PERMISSIONS.CALL_SVC_ADMIN_BASE]}
            permissionMode={CheckPermissionsModes.ANY}
            fallback={<NotAuthorizedPage />}
        >
            <div className="flex h-full flex-col gap-4 p-4">
                <TopBar
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    onOpenThresholds={() => setIsThresholdsOpen(true)}
                />

                <SettingsPanel
                    teams={teams}
                    agents={visibleAgents}
                    dimensions={selectableDimensions}
                    selectedTeamIds={settings.selectedTeamIds}
                    selectedAgentIds={settings.selectedAgentIds}
                    selectedDimensionIds={selectedDimensionIds}
                    showSelectedAverage={settings.showSelectedAverage}
                    onSelectedTeamIdsChange={handleSelectedTeamIdsChange}
                    onSelectedAgentIdsChange={(selectedAgentIds) =>
                        setSettings((prev) => ({ ...prev, selectedAgentIds }))
                    }
                    onSelectedDimensionIdsChange={(selectedDimensionIds) =>
                        setSettings((prev) => ({
                            ...prev,
                            selectedDimensionIds: sanitizeSelectedMatrixDimensionIds(selectedDimensionIds),
                        }))
                    }
                    onShowSelectedAverageChange={(showSelectedAverage) =>
                        setSettings((prev) => ({ ...prev, showSelectedAverage }))
                    }
                />

                {viewMode === "hour" && utcRange?.hourViewUsesFirstDayOnly ? (
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                        {t("agent_quality_matrix_hour_multi_day_hint")}
                    </p>
                ) : null}

                <div className="flex flex-wrap items-center justify-end gap-3 text-[11px]">
                    <span className="text-muted-foreground">{t("legend")}:</span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded bg-red-500/80" />
                        <span className="inline-flex items-center gap-1.5">
                            <span className="h-3 w-3 rounded bg-amber-400/80" />
                            <span className="h-3 w-3 rounded bg-emerald-500/80" />
                        </span>
                        <span>{t("agent_quality_matrix_legend_kpi_tiers")}</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded bg-primary/30 ring-1 ring-foreground/20" />
                        <span>
                            <strong>x̄</strong> — {t("agent_quality_matrix_legend_overall")}
                        </span>
                    </span>
                    {canShowSelectedAverageColumn ? (
                        <span className="inline-flex items-center gap-1.5 opacity-70">
                            <span className="h-3 w-3 rounded bg-primary/10 ring-1 ring-foreground/20" />
                            <span>
                                <strong>x̄ˢ</strong> — {t("agent_quality_matrix_legend_selected_avg")}
                            </span>
                        </span>
                    ) : null}
                </div>

                <AgentQualityMatrixTable
                    rows={agentRows}
                    segments={segments}
                    dimensions={tableDimensions}
                    reportData={reportData}
                    isReportLoading={isReportLoading}
                    isReportError={isReportError}
                    reportErrorMessage={reportErrorMessage}
                    kpiThresholds={settings.kpiThresholds}
                    sortState={sortState}
                    onSortChange={setSortState}
                    rowLabel="Agent"
                    emptyMessage={tableEmptyMessage}
                    overallDimensionName={overallDimension?.name}
                    totalSelectableDimensions={selectableDimensions.length}
                    showSelectedAverage={settings.showSelectedAverage}
                />

                <p className="text-[11px] text-muted-foreground">{t("agent_quality_matrix_footer")}</p>

                <ThresholdsDialog
                    open={isThresholdsOpen}
                    onOpenChange={setIsThresholdsOpen}
                    kpiThresholds={settings.kpiThresholds}
                    onSave={(next) => setSettings((prev) => ({ ...prev, ...next }))}
                />
            </div>
        </AuthGuard>
    );
}
